/**
 * Alfred's settings screen (web `/alfred`; iOS/Android `alfredSettings`): what the screen shows for a state the
 * server handed it. Import-free so jest can exercise it, and mirrored 1:1 by iOS `AlfredSettings.swift` and
 * Android `AlfredSettings.kt` — same view rules, same summaries, same prompt and BI names.
 *
 * The state is the server's (`/api/connectors/alfred-settings`, native-data `alfred-settings`), and it is
 * read-only: every change the screen offers is a chat handoff, where the `using-alfred` skill shows the change
 * and waits for a yes. Nothing here decides anything about Alfred, only how a state is drawn.
 */
import type { AlfredForwardingDialCodes, AlfredForwardingMode } from './alfred-setup';

/** The settings screen's route. Reached from Manage on the connected Outreach Alfred row; no app deeplink yet. */
export const ALFRED_SETTINGS_PATH = '/alfred';

export type AlfredStatus = 'active' | 'paused' | 'off';
export type AlfredTone = 'warm' | 'professional' | 'energetic' | 'calm';
export type AlfredGender = 'male' | 'female';

/** How callers hear it. A blank or unset value is `null` (the server normalizes it). */
export interface AlfredVoice {
  assistantName: string | null;
  tone: AlfredTone | null;
  gender: AlfredGender | null;
  greetingMessage: string | null;
}

export interface AlfredSettings {
  status: AlfredStatus;
  /** E.164 — what Copy copies and Test call dials. */
  phoneNumber: string | null;
  /** The same number written for reading. */
  displayPhoneNumber: string | null;
  forwarding: AlfredForwardingDialCodes | null;
  voice: AlfredVoice;
}

/**
 * - `notConnected` — Alfred is not installed (`null`), or never got as far as a number: nothing answers.
 * - `paused` — installed with a number, but not answering (PAUSED or OFF).
 * - `active` — answering.
 */
export type AlfredSettingsView = 'notConnected' | 'paused' | 'active';

export function alfredSettingsView(settings: AlfredSettings | null): AlfredSettingsView {
  if (!settings || !settings.phoneNumber) return 'notConnected';
  return settings.status === 'active' ? 'active' : 'paused';
}

const COPY = 'connectors.channelsOut.alfred.settings.';

/** The header's status chip: green while answering, neutral otherwise. Never drawn for `notConnected`. */
export function alfredStatusBadge(status: AlfredStatus): { variant: 'active' | 'paused'; labelKey: string } {
  return { variant: status === 'active' ? 'active' : 'paused', labelKey: `${COPY}status.${status}` };
}

/** The Personality & knowledge rows, in the order the design draws them. */
export type AlfredSettingsRow = 'persona' | 'voice' | 'greeting' | 'knowledge';

export const ALFRED_SETTINGS_ROWS: readonly AlfredSettingsRow[] = ['persona', 'voice', 'greeting', 'knowledge'];

/** Every chat handoff the screen makes: the three rows Alfred's chat can change, and the forwarding card's
 *  "Landline or VoIP?". Business knowledge is not one of them — see {@link alfredRowOpensKnowledge}. */
export type AlfredSettingsHandoff = Exclude<AlfredSettingsRow, 'knowledge'> | 'forwarding';

/**
 * Where Business knowledge is changed: Symphony's own Knowledge screen. Alfred speaks from the account's
 * `knowledge/business/*` — that screen's Business group — so the row opens it rather than a chat, which reaches
 * the same files only if the agent happens to read the request that way.
 */
export const ALFRED_KNOWLEDGE_PATH = '/knowledge';

export const alfredRowOpensKnowledge = (row: AlfredSettingsRow): row is 'knowledge' => row === 'knowledge';

export const alfredRowTitleKey = (row: AlfredSettingsRow): string => `${COPY}${row}.title`;

/** The translated message a handoff seeds a NEW chat with. */
export const alfredChatPromptKey = (handoff: AlfredSettingsHandoff): string => `${COPY}chat.${handoff}`;

/** The click a handoff reports, the same name on all three surfaces. */
export const alfredChatBiName = (handoff: AlfredSettingsHandoff): string => `alfred_settings_chat_${handoff}`;

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** A number or dial code inside a translated sentence, isolated left-to-right (LRI…PDI) so an RTL sentence
 *  keeps it in one piece. Only the shown text: Copy copies the bare value. */
export const ltrIsolate = (text: string): string => `⁦${text}⁩`;

function toneLabel(voice: AlfredVoice, t: Translate): string | null {
  return voice.tone ? t(`${COPY}tone.${voice.tone}`) : null;
}

/** "Maya · Calm" — the name callers hear and the tone, whichever are set. */
export function alfredPersonaSummary(voice: AlfredVoice, t: Translate): string {
  const parts = [voice.assistantName, toneLabel(voice, t)].filter((part): part is string => !!part);
  return parts.length > 0 ? parts.join(' · ') : t(`${COPY}notSet`);
}

export function alfredVoiceSummary(voice: AlfredVoice, t: Translate): string {
  return voice.gender ? t(`${COPY}voice.${voice.gender}`) : t(`${COPY}notSet`);
}

/** The greeting word for word, quoted; with none set, Alfred speaks its own default. */
export function alfredGreetingSummary(voice: AlfredVoice, t: Translate): string {
  return voice.greetingMessage ? t(`${COPY}greeting.quoted`, { greeting: voice.greetingMessage }) : t(`${COPY}greeting.default`);
}

export function alfredRowSummary(row: AlfredSettingsRow, voice: AlfredVoice, t: Translate): string {
  switch (row) {
    case 'persona':
      return alfredPersonaSummary(voice, t);
    case 'voice':
      return alfredVoiceSummary(voice, t);
    case 'greeting':
      return alfredGreetingSummary(voice, t);
    case 'knowledge':
      return t(`${COPY}knowledge.description`);
  }
}

/** The codes that undo the chosen way of forwarding; `null` when the server sent none to offer. */
export function alfredTurnOffCodes(
  forwarding: AlfredForwardingDialCodes | null,
  mode: AlfredForwardingMode,
): { code: string; verizonCode: string } | null {
  const codes = forwarding?.[mode];
  if (!codes?.standardOff || !codes.verizonOff) return null;
  return { code: codes.standardOff, verizonCode: codes.verizonOff };
}
